import { useNavigate } from '@tanstack/react-router'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Progress } from '@/components/ui/progress'
import { StatusBadge } from './StatusBadge'
import { getInitials } from './collaborator-utils'
import { useBonificacaoStore } from '@/stores/bonificacao-store'
import { usePublishAll } from '@/api/bonificacao-hooks'
import type { BonusAssessmentSummary } from '@/api/bonificacao-hooks'

interface CollaboratorCardProps {
  assessment: BonusAssessmentSummary
  /** When true, shows RH-only controls like "Publicar Tudo" */
  isRH?: boolean
}

export function CollaboratorCard({ assessment, isRH }: CollaboratorCardProps) {
  const navigate = useNavigate()
  const publishAll = usePublishAll()

  const handleClick = () => {
    useBonificacaoStore.getState().setSelectedEmployee(assessment.employeeId)
    useBonificacaoStore.getState().setAssessment(assessment.assessmentId)
    useBonificacaoStore.getState().setSelectedEmployeeInfo({
      name: assessment.employeeName,
      department: assessment.department,
      pictureUrl: assessment.pictureUrl,
    })
    navigate({
      to: '/performance/bonificacao/metas',
      search: { employee: assessment.employeeId },
    })
  }

  const score = assessment.scoreGeral ?? 0
  const hasBonusCalc = assessment.totalSalarios != null

  const isUnknown = !assessment.employeeName || assessment.employeeName === 'Unknown'

  return (
    <div
      className="card-interactive p-4 space-y-3 cursor-pointer motion-safe:transition-all motion-safe:duration-200 hover:scale-[1.02] hover:shadow-monumenta-lg"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-label={`Ver avaliação de ${isUnknown ? 'colaborador não identificado' : assessment.employeeName}`}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick() }}
      data-testid={`collaborator-card-${assessment.employeeId}`}
    >
      <div className="flex items-start gap-3">
        <Avatar className="size-12">
          {assessment.pictureUrl && (
            <AvatarImage src={assessment.pictureUrl} alt={assessment.employeeName} />
          )}
          <AvatarFallback className={`text-sm ${isUnknown ? 'bg-brand-dark-lighter/50 text-brand-gray/50' : 'bg-brand-dark-lighter text-brand-gray'}`}>
            {isUnknown ? '?' : getInitials(assessment.employeeName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className={`font-display truncate ${isUnknown ? 'text-brand-gray/60 italic font-normal' : 'font-bold text-white'}`}>
            {isUnknown ? 'Colaborador não identificado' : assessment.employeeName}
          </p>
          <p className="text-sm text-brand-gray truncate">
            {assessment.role}
          </p>
          {assessment.department && (
            <p className="text-xs text-brand-gray mt-1">
              {assessment.department}
            </p>
          )}
        </div>
      </div>

      <StatusBadge status={assessment.status} />

      <div className="space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="text-brand-gray">Score</span>
          <span className="text-white font-medium">{score}%</span>
        </div>
        <Progress value={score} className="h-2" />
      </div>

      <div className="flex items-center justify-between pt-1">
        <div>
          {hasBonusCalc ? (
            <span className="text-brand-primary font-display font-bold text-lg">
              {assessment.totalSalarios!.toFixed(2)} <span className="text-sm font-normal text-brand-gray">sal.</span>
            </span>
          ) : (
            <span className="text-brand-gray text-sm">Sem cálculo</span>
          )}
        </div>
        {isRH && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              publishAll.mutate({ assessmentId: assessment.assessmentId })
            }}
            className="text-xs px-2.5 py-1 rounded-md bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 transition-colors font-medium"
            data-testid={`publish-all-${assessment.assessmentId}`}
          >
            {publishAll.isPending ? 'Publicando...' : 'Publicar Tudo'}
          </button>
        )}
      </div>
    </div>
  )
}
